import { SearchInput } from '@/components/ui/search-input'
import { NAVIGATION_ICONS } from '../shared/navigation-config'
import { PanelEmptyState } from '../shared/panel-empty-state'
import { useLingui } from '@lingui/react/macro'
import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { useParams } from '@tanstack/react-router'
import { useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { useDeferredValue, useMemo } from 'react'
import { ApplicantOfferTreeItem } from './applicant-offer-tree-item'
import { SectionLabel } from '@/components/custom/section-label'
import { ScrollableList } from '../shared/scrollable-list'
import { AnimatedList } from '../shared/animated-list'
import { AlignedSortFilterGroups } from '../shared/aligned-sort-filter-groups'
import { SortByAdjustButtons } from '../shared/sort-button'
import { SelectByButtons } from '../shared/select-by-buttons'
import type { RootSearch } from '@/routes/__root'

/**
 * SINGLE SOURCE of the applicant `getOffersInfinite` input — used by the loader prefetch, the
 * `ApplicantOffersLayout` `hasOffers` read, AND this panel's list read, so all three share ONE cache
 * key. Previously the layout and loader hardcoded `{ q: '', o: 'added', type: 'competences' }` while the
 * panel derived `o`/`type` from search, so changing the sort or select-by spawned a SECOND cache entry
 * (double fetch + a `hasOffers` count computed from a different list than the one on screen, + a wasted
 * prefetch on a reload with a non-default `type`). `o` defaults to `'added'` and `type` to `'competences'`
 * (root schema), so on a cold load with no search params this equals the old hardcoded literal.
 */
export function applicantOffersInput(search: Pick<RootSearch, 'q' | 'o' | 'type'>) {
  return { q: search.q, o: search.o ?? 'added', type: search.type } as const
}

export function applicantOffersInfiniteQueryOptions(input: ReturnType<typeof applicantOffersInput>) {
  return trpc.applicants.getOffersInfinite.infiniteQueryOptions(input, {
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  })
}

/**
 * The ONE way to read the applicant offers list. The input is deferred so a search/sort/type change keeps
 * the previous list on screen while the new page loads (keep-previous UX). Both the layout (`hasOffers`)
 * and the panel call this, sharing one cache entry — no divergent key, no double fetch.
 */
export function useApplicantOffersInfinite() {
  const { q, o, type } = useRootSearch()
  const input = useMemo(() => applicantOffersInput({ q, o, type }), [q, o, type])
  const deferredInput = useDeferredValue(input)
  const query = useSuspenseInfiniteQuery(applicantOffersInfiniteQueryOptions(deferredInput))
  // True while a search/sort/type change is mid-transition (deferred input still trailing the live one).
  const isFetching = query.isFetching || deferredInput !== input
  return { query, isFetching, activeQuery: deferredInput.q }
}

export function ApplicantOffersPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { t } = useLingui()
  const { offerId } = useParams({ strict: false })
  const { q: searchQueryFromUrl, type: typeFromUrl } = useRootSearch()

  const { searchInput, handleSearchChange } = useDebouncedSearch({
    initialValue: searchQueryFromUrl,
  })

  const { query, isFetching: isOffersFetching, activeQuery } = useApplicantOffersInfinite()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = query

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  // Flatten all pages
  const allOffers = data.pages.flatMap((page) => page.list)

  const addedOffers = allOffers.filter((offer) => offer.added)
  const suggestedOffers = allOffers.filter((offer) => !offer.added)
  const addedOffersCount = addedOffers.length
  const suggestedOffersCount = suggestedOffers.length

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    // Main panel variant - full UI adapted for main content area
    return (
      <div className="flex h-full flex-col">
        {/* Search bar */}
        <div className="flex items-center gap-2 border-b p-3">
          <SearchInput
            placeholder={t`Search for Offers...`}
            value={searchInput}
            onChange={handleSearchChange}
            isLoading={isOffersFetching && searchQueryFromUrl.length > 0}
          />
        </div>

        {/* Select by and Sort options */}
        <div className="flex items-center gap-4 border-b px-4 py-3">
          <AlignedSortFilterGroups
            direction="vertical"
            groups={[
              {
                label: t`Select`,
                children: <SelectByButtons currentSelect={typeFromUrl} />,
              },
              {
                label: t`Sort by`,
                children: <SortByAdjustButtons />,
              },
            ]}
          />
        </div>

        {/* Offers list */}
        <ScrollableList
          isFetching={isOffersFetching}
          loadMoreRef={loadMoreRef}
          isFetchingMore={isFetchingNextPage}
          isEmpty={allOffers.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allOffers.length === 0 && !!activeQuery}
              icon={<NAVIGATION_ICONS.offers className="text-muted-foreground size-5" />}
              title={t`No offers added yet`}
              description={t`Search for offers above to find and add them`}
            />
          }
        >
          {(addedOffers.length > 0 || suggestedOffers.length === 0) && (
            <SectionLabel className="px-2">
              {addedOffersCount === 1 ? t`${addedOffersCount} offer` : t`${addedOffersCount} offers`}
            </SectionLabel>
          )}
          <AnimatedList className="gap-1">
            {addedOffers.map((offer) => (
              <ApplicantOfferTreeItem
                key={offer.id}
                offer={offer}
                isSelected={offerId === offer.id.toString()}
                currentSelectType={typeFromUrl}
              />
            ))}
          </AnimatedList>

          {/* Suggested offers section */}
          {suggestedOffers.length > 0 && (
            <>
              {addedOffers.length > 0 && (
                <SectionLabel className="px-2">
                  {suggestedOffersCount === 1
                    ? t`${suggestedOffersCount} you might be interested in`
                    : t`${suggestedOffersCount} you might be interested in`}
                </SectionLabel>
              )}
              <AnimatedList className="gap-1">
                {suggestedOffers.map((offer) => (
                  <ApplicantOfferTreeItem
                    key={offer.id}
                    offer={offer}
                    isSelected={offerId === offer.id.toString()}
                    currentSelectType={typeFromUrl}
                  />
                ))}
              </AnimatedList>
            </>
          )}
        </ScrollableList>
      </div>
    )
  }

  // Sidebar variant - full UI with search/filters
  return (
    <>
      {/* Search bar */}
      <div className="flex items-center gap-2 border-b p-3">
        <SearchInput
          placeholder={t`Search for Offers...`}
          value={searchInput}
          onChange={handleSearchChange}
          isLoading={isOffersFetching && searchQueryFromUrl.length > 0}
        />
      </div>

      {/* Select by and Sort options */}
      <div className="border-b px-4 py-3">
        <AlignedSortFilterGroups
          direction="vertical"
          groups={[
            {
              label: t`Select`,
              children: <SelectByButtons currentSelect={typeFromUrl} />,
            },
            {
              label: t`Sort by`,
              children: <SortByAdjustButtons />,
            },
          ]}
        />
      </div>

      <ScrollableList
        isFetching={isOffersFetching}
        loadMoreRef={loadMoreRef}
        isFetchingMore={isFetchingNextPage}
        isEmpty={allOffers.length === 0}
        emptyState={
          <PanelEmptyState
            noResults={allOffers.length === 0 && !!activeQuery}
            icon={<NAVIGATION_ICONS.offers className="text-muted-foreground size-5" />}
            title={t`No offers added yet`}
            description={t`Search for offers above to find and add them`}
          />
        }
      >
        {/* Added offers section */}
        {addedOffers.length > 0 &&
          addedOffers.map((offer) => (
            <ApplicantOfferTreeItem
              key={offer.id}
              offer={offer}
              isSelected={offerId === offer.id.toString()}
              currentSelectType={typeFromUrl}
            />
          ))}

        {/* Suggested offers section */}
        {suggestedOffers.length > 0 && (
          <>
            <SectionLabel className="px-2">
              {suggestedOffersCount === 1
                ? t`${suggestedOffersCount} you might be interested in`
                : t`${suggestedOffersCount} you might be interested in`}
            </SectionLabel>
            {suggestedOffers.map((offer) => (
              <ApplicantOfferTreeItem
                key={offer.id}
                offer={offer}
                isSelected={offerId === offer.id.toString()}
                currentSelectType={typeFromUrl}
              />
            ))}
          </>
        )}
      </ScrollableList>
    </>
  )
}
